import { log } from 'utils/logger'
import { DriverNoteDTO } from '@numeo/types'
import { fetchDriverNotesBatch, BatchDriverNoteItem } from './driverNotesApi'

/**
 * Bounded response LRU shared by hook instances: reads/writes promote a context and evict the oldest.
 * A 700-driver board typically uses 700–900 driver/load/truck contexts per pass. Capacity 1,000
 * keeps that pass cached while bounding a working day's accumulation of up to 50 notes per context.
 * Above capacity, older contexts are refetched on remount; mounted rows retain their visible notes.
 * No TTL is needed: mutations apply deltas, and reconnect explicitly refreshes mounted contexts.
 */
export const NOTES_CACHE_MAX_SIZE = 1000
const _notesCache = new Map<string, DriverNoteDTO[]>()
// A full batch snapshot can satisfy a concurrent failed read; a one-note mutation result cannot.
// Weak references retain no arrays after eviction or replacement.
let fetchedSnapshots = new WeakSet<DriverNoteDTO[]>()

/** Scheduling hints propagated through cache invalidation subscribers. */
export interface DriverNoteFetchOptions {
    retry?: number
    failure?: { retrying: boolean; cause: unknown }
    refreshed?: boolean
    notBefore?: number
    subscribedOnly?: boolean
}

/** Subscriber callbacks per cache key — called when cache entry changes */
const _subscribers = new Map<string, Set<(options?: DriverNoteFetchOptions) => void>>()

/** Pending items waiting to be batched in the next flush */
const _pendingBatchItems = new Map<string, { item: BatchDriverNoteItem; retry: number; notBefore: number; subscribedOnly: boolean }>()

/** Timer handle for the batch debounce window */
let _batchTimer: ReturnType<typeof setTimeout> | null = null
let batchScheduledAt = 0
let backgroundNotBefore = 0

const BATCH_DEBOUNCE_MS = 50
const MAX_BATCH_ITEMS = 100
const MAX_CONCURRENT_BATCHES = 2
let activeBatches = 0
let cacheGeneration = 0
type NoteChange = { note: DriverNoteDTO; deleted: boolean; sequence: number }
const latestNoteUpdates = new Map<string, NoteChange>()
let noteSequence = 0
// Independent tab-local jitter spreads recovery reads and their one retry.
const backgroundFetchOptions = (retry = 0): DriverNoteFetchOptions => {
    const now = Date.now()
    if (backgroundNotBefore <= now) backgroundNotBefore = now + 250 + Math.floor(Math.random() * 1000)
    return { retry, notBefore: backgroundNotBefore, subscribedOnly: true }
}
// Only in-flight drivers need revision state; removed when their last request completes.
const inFlightDrivers = new Map<string, { revision: number; requests: number; contexts: Map<string, number> }>()

/** Reads a cache entry and marks it most-recently-used. Returns undefined on a miss. */
export function cacheGet(key: string): DriverNoteDTO[] | undefined {
    const value = _notesCache.get(key)
    if (value === undefined) return undefined
    _notesCache.delete(key)
    _notesCache.set(key, value)
    return value
}

/** Capture the canonical context and the last delta it already includes before an optimistic write. */
export const captureDriverNoteContext = (key: string, visibleNotes: DriverNoteDTO[]) => ({ key, notes: cacheGet(key) ?? visibleNotes, sequence: noteSequence })

/** Writes a cache entry, marking it most-recently-used, evicting the LRU entry if full. */
export function cacheSet(key: string, notes: DriverNoteDTO[]): void {
    if (_notesCache.has(key)) {
        _notesCache.delete(key)
    } else if (_notesCache.size >= NOTES_CACHE_MAX_SIZE) {
        const oldestKey = _notesCache.keys().next().value
        if (oldestKey !== undefined) _notesCache.delete(oldestKey)
    }
    _notesCache.set(key, notes)
}

function notifySubscribers(key: string, options?: DriverNoteFetchOptions) {
    const subs = _subscribers.get(key)
    if (subs) subs.forEach((cb) => cb(options))
}

function scheduleBatch(notBefore: number) {
    if (_batchTimer !== null && batchScheduledAt <= notBefore) return
    if (_batchTimer !== null) clearTimeout(_batchTimer)
    batchScheduledAt = notBefore
    _batchTimer = setTimeout(flushBatch, Math.max(0, notBefore - Date.now()))
}

async function flushBatch() {
    _batchTimer = null
    if (activeBatches >= MAX_CONCURRENT_BATCHES) return
    const generation = cacheGeneration
    const now = Date.now()
    const entries = []
    let nextDeadline = Infinity
    for (const [key, entry] of _pendingBatchItems) {
        if (entry.subscribedOnly && !_subscribers.has(key)) {
            _pendingBatchItems.delete(key)
        } else if (entry.notBefore <= now && entries.length < MAX_BATCH_ITEMS) {
            entries.push([key, entry] as const)
            _pendingBatchItems.delete(key)
        } else {
            nextDeadline = Math.min(nextDeadline, entry.notBefore)
        }
    }
    if (Number.isFinite(nextDeadline)) scheduleBatch(nextDeadline)
    if (entries.length === 0) return

    activeBatches++
    const requestRevisions = new Map<string, number>()
    for (const [key, { item }] of entries) {
        const state = inFlightDrivers.get(item.driverId) ?? { revision: 0, requests: 0, contexts: new Map<string, number>() }
        state.contexts.set(key, (state.contexts.get(key) ?? 0) + 1)
        if (requestRevisions.has(item.driverId)) continue
        state.requests += 1
        inFlightDrivers.set(item.driverId, state)
        requestRevisions.set(item.driverId, state.revision)
    }
    try {
        const requestSequence = noteSequence
        const items = entries.map(([, { item }]) => item)
        const results = await fetchDriverNotesBatch(items)
        if (generation !== cacheGeneration) return
        for (const [key, { item }] of entries) {
            if (requestRevisions.get(item.driverId) !== inFlightDrivers.get(item.driverId)?.revision) {
                // A one-note HTTP result cannot prove the context contains concurrent remote notes.
                if (_subscribers.has(key)) enqueueForBatch(key, item, backgroundFetchOptions())
                continue
            }
            const notes = replayNoteChanges(key, results[key] || [], requestSequence)
            cacheSet(key, notes)
            fetchedSnapshots.add(notes)
            notifySubscribers(key, { refreshed: true })
        }
    } catch (e) {
        if (generation !== cacheGeneration) return
        log.e('Batch fetch driver notes failed', e, { contextCount: entries.length })
        for (const [key, { item, retry }] of entries) {
            const current = _notesCache.get(key)
            if (!_subscribers.has(key) || _pendingBatchItems.has(key) || (current && fetchedSnapshots.has(current))) continue
            notifySubscribers(key, { failure: { retrying: retry === 0, cause: e } })
            if (retry === 0) enqueueForBatch(key, item, backgroundFetchOptions(1))
        }
    } finally {
        if (generation === cacheGeneration) {
            for (const [key, { item }] of entries) {
                const contexts = inFlightDrivers.get(item.driverId)?.contexts
                if (contexts?.get(key) === 1) contexts.delete(key)
                else if (contexts?.has(key)) contexts.set(key, contexts.get(key)! - 1)
            }
            for (const driverId of requestRevisions.keys()) {
                const state = inFlightDrivers.get(driverId)
                if (state && --state.requests === 0) inFlightDrivers.delete(driverId)
            }
            activeBatches--
            if (_pendingBatchItems.size && _batchTimer === null) scheduleBatch(Date.now())
        }
    }
}

export function enqueueForBatch(key: string, item: BatchDriverNoteItem, options: DriverNoteFetchOptions = {}) {
    // Repeated events share the first deadline, so continuous edits cannot postpone a row forever.
    const initialDeadline = Date.now() + BATCH_DEBOUNCE_MS
    const previous = _pendingBatchItems.get(key)
    const requestedDeadline = options.notBefore ?? (_batchTimer !== null ? Math.min(initialDeadline, batchScheduledAt) : initialDeadline)
    const notBefore = previous ? Math.min(previous.notBefore, requestedDeadline) : requestedDeadline
    _pendingBatchItems.set(key, {
        item,
        retry: Math.max(previous?.retry ?? 0, options.retry ?? 0),
        notBefore,
        subscribedOnly: (previous?.subscribedOnly ?? true) && (options.subscribedOnly ?? false),
    })
    scheduleBatch(notBefore)
}

const mergeNoteChange = (key: string, notes: DriverNoteDTO[], { note, deleted }: Pick<NoteChange, 'note' | 'deleted'>): DriverNoteDTO[] => {
    if (!key.startsWith(`[${JSON.stringify(note.driverId)},`)) return notes
    const context: unknown = JSON.parse(key)
    if (!Array.isArray(context)) return notes
    const existing = notes.find((item) => item.id === note.id)
    if (existing && (existing.version ?? 0) > (note.version ?? 0)) return notes
    const belongs = (note.loadId || '') === context[1]
    if (!existing && (deleted || !belongs)) return notes
    const next = notes.filter((item) => item.id !== note.id)
    if (!deleted && belongs) next.push({ ...note, authorName: note.authorName || existing?.authorName })
    return next.sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)).slice(0, 50)
}

const replayNoteChanges = (key: string, notes: DriverNoteDTO[], afterSequence: number): DriverNoteDTO[] => {
    let merged = notes
    for (const update of latestNoteUpdates.values()) {
        if (update.sequence > afterSequence) merged = mergeNoteChange(key, merged, update)
    }
    return merged
}

/** Apply the event payload now. Only reads that started before this change replay it. */
export const applyDriverNoteUpdate = (change: Pick<NoteChange, 'note' | 'deleted'>, fallback?: ReturnType<typeof captureDriverNoteContext>): void => {
    // A mounted row can outlive its LRU entry while its HTTP mutation is pending.
    // Restore that view through this owner, replaying known deltas before merging the response.
    let restoredKey: string | undefined
    if (fallback && !_notesCache.has(fallback.key)) {
        const restored = replayNoteChanges(fallback.key, fallback.notes, fallback.sequence)
        cacheSet(fallback.key, restored)
        restoredKey = fallback.key
    }
    const previous = latestNoteUpdates.get(change.note.id)
    if (previous && (previous.note.version ?? 0) >= (change.note.version ?? 0)) {
        if (restoredKey) notifySubscribers(restoredKey)
        return
    }
    latestNoteUpdates.delete(change.note.id)
    latestNoteUpdates.set(change.note.id, { ...change, sequence: ++noteSequence })
    if (latestNoteUpdates.size > NOTES_CACHE_MAX_SIZE) latestNoteUpdates.delete(latestNoteUpdates.keys().next().value!)
    for (const key of new Set([..._notesCache.keys(), ..._subscribers.keys()])) {
        const notes = _notesCache.get(key)
        if (notes === undefined) {
            // A delta cannot reconstruct an evicted list. Retain the mounted view until a full read lands.
            if (
                key.startsWith(`[${JSON.stringify(change.note.driverId)},`) &&
                !_pendingBatchItems.has(key) &&
                !inFlightDrivers.get(change.note.driverId)?.contexts.has(key)
            ) {
                notifySubscribers(key, backgroundFetchOptions())
            }
            continue
        }
        const next = mergeNoteChange(key, notes, change)
        if (next === notes) continue
        cacheSet(key, next)
        notifySubscribers(key)
    }
}

export function invalidateDriverNotes(driverIds: string | string[]): void {
    const affectedDrivers = new Set(typeof driverIds === 'string' ? [driverIds] : driverIds)
    if (!affectedDrivers.size) return
    for (const driverId of affectedDrivers) {
        const inFlight = inFlightDrivers.get(driverId)
        if (inFlight) inFlight.revision += 1
    }
    const keys = new Set([..._notesCache.keys(), ..._subscribers.keys()])
    const options = backgroundFetchOptions()
    for (const key of keys) {
        const [driverId] = JSON.parse(key) as string[]
        if (!affectedDrivers.has(driverId)) continue
        _notesCache.delete(key)
        notifySubscribers(key, options)
    }
}

/** Re-read only mounted contexts after a disconnected socket may have missed events. */
export const refreshNotesAfterReconnect = (): void => {
    cacheGeneration++
    activeBatches = 0
    inFlightDrivers.clear()
    latestNoteUpdates.clear()
    _notesCache.clear()
    _pendingBatchItems.clear()
    if (_batchTimer) clearTimeout(_batchTimer)
    _batchTimer = null
    for (const key of _subscribers.keys()) {
        const context: unknown = JSON.parse(key)
        if (!Array.isArray(context) || typeof context[0] !== 'string') continue
        enqueueForBatch(
            key,
            {
                driverId: context[0],
                loadId: typeof context[1] === 'string' ? context[1] || undefined : undefined,
                truckId: typeof context[2] === 'string' ? context[2] || undefined : undefined,
            },
            backgroundFetchOptions()
        )
    }
}

/** Clear tenant note state on logout or workspace changes. */
export const resetDriverNotes = (): void => {
    cacheGeneration++
    latestNoteUpdates.clear()
    noteSequence = 0
    activeBatches = 0
    inFlightDrivers.clear()
    _notesCache.clear()
    fetchedSnapshots = new WeakSet()
    _subscribers.clear()
    _pendingBatchItems.clear()
    batchScheduledAt = 0
    backgroundNotBefore = 0
    if (_batchTimer) {
        clearTimeout(_batchTimer)
        _batchTimer = null
    }
}

/** Read without promoting a context during an uncommitted React render. */
export const peekDriverNotes = (key: string): DriverNoteDTO[] | undefined => _notesCache.get(key)

export const subscribeDriverNotes = (key: string, callback: (options?: DriverNoteFetchOptions) => void): (() => void) => {
    if (!_subscribers.has(key)) _subscribers.set(key, new Set())
    _subscribers.get(key)!.add(callback)
    return () => {
        _subscribers.get(key)?.delete(callback)
        if (!_subscribers.get(key)?.size) _subscribers.delete(key)
    }
}
